import React, { useEffect, useSyncExternalStore } from 'react'
import PropTypes from 'prop-types'
import exact from 'prop-types-exact'
import { useSelector } from 'react-redux'
import { EtuoviReactEmbed } from '@almamedia/alma-embeds/dist/etuovi'
import { NettiautoColumnEmbed } from '@almamedia/alma-embeds/dist/nettiauto-column'
import { ILUutisboksi } from '@almamedia/alma-embeds/dist/il-uutisboksi'
import { KLUutisboksi } from '@almamedia/alma-embeds/dist/kl-uutisboksi'
import { selectUserABTestVariant, selectUserActiveTheme } from '../../selectors/userSelector'
import {
  selectTrafficSteeringBrand,
  selectTrafficSteeringIlCategories,
} from '../../selectors/trafficSteeringSelector'
import {
  BRAND_ETUOVI,
  BRAND_ILTALEHTI,
  BRAND_KAUPPALEHTI,
  BRAND_NETTIAUTO,
} from '../../lib/trafficSteering'
import './traffic-steering.pcss'

const UTM_SOURCE = 'ampparit'
const DARK_THEMES = ['night', 'matrix']

/** Embeds touch window on mount, so they must not render during SSR. */
const subscribeToNothing = () => () => {}
const isClient = () => true
const isServer = () => false

function PartnerEmbed({ brand, ilCategories }) {
  switch (brand) {
    case BRAND_ETUOVI:
      return <EtuoviReactEmbed utmSource={ UTM_SOURCE } />
    case BRAND_NETTIAUTO:
      return <NettiautoColumnEmbed utmSource={ UTM_SOURCE } />
    case BRAND_ILTALEHTI:
      return (
        <ILUutisboksi
          utmSource={ UTM_SOURCE }
          darkThemeEnabled
          { ...(ilCategories ? { categories: ilCategories } : {}) }
        />
      )
    case BRAND_KAUPPALEHTI:
      return <KLUutisboksi utmSource={ UTM_SOURCE } />
    default:
      return null
  }
}

PartnerEmbed.propTypes = exact({
  brand: PropTypes.string.isRequired,
  ilCategories: PropTypes.arrayOf(PropTypes.string),
})

/**
 * Contextual Alma partner box. Renders only when the A/B variant matches `placement`
 * and the current page maps to a target brand.
 */
const TrafficSteeringBox = ({ placement }) => {
  const variant = useSelector(selectUserABTestVariant)
  const brand = useSelector(selectTrafficSteeringBrand)
  const ilCategories = useSelector(selectTrafficSteeringIlCategories)
  const isDarkTheme = DARK_THEMES.includes(useSelector(selectUserActiveTheme))
  const canRenderEmbed = useSyncExternalStore(subscribeToNothing, isClient, isServer)

  const visible = Boolean(brand) && variant === placement

  // Alma-embeds read their theme from html.theme-light / html.theme-dark.
  useEffect(() => {
    if (!visible) {
      return
    }
    const { classList } = window.document.documentElement
    classList.toggle('theme-dark', isDarkTheme)
    classList.toggle('theme-light', !isDarkTheme)
  }, [visible, isDarkTheme])

  if (!visible || !canRenderEmbed) {
    return null
  }

  return (
    <div className='traffic-steering-box' data-brand={ brand } data-placement={ placement }>
      <PartnerEmbed brand={ brand } ilCategories={ ilCategories } />
    </div>
  )
}

TrafficSteeringBox.propTypes = exact({
  placement: PropTypes.oneOf(['a', 'b']).isRequired,
})

export default TrafficSteeringBox
